// DeskeyHUB — student-facing library.
//
// User intent (verbatim): "deskey hub is only for the teacher and department
// to create subject with subject code and publish book materials under that
// subject by editing file name and this can be .doc,pdf,ppt … student can
// access institution published books via this and question banks as well …
// documents, pdf, ppt should be online viewable not only downloadable."
//
// Three-level drill-down:
//   1. Departments  →  2. Subjects (with subject code)  →  3. Materials
//
// Materials open inline in `DocViewer` (PDF iframe, Google Docs viewer for
// .doc/.docx/.ppt/.pptx, image preview, etc.) — never forced to download.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './studentApi';
import { useStudent } from './StudentApp';
import { Input } from '@/components/ui/input';
import {
  Library, ArrowLeft, Search, FileText, BookOpen, Eye, Download, FileType,
  Calendar, GraduationCap, Users, ArrowRight,
} from 'lucide-react';
import DocViewer from '../../components/DocViewer';

const FILE_TYPE_COLORS = {
  pdf:  '#dc2626',
  doc:  '#2563eb', docx: '#2563eb',
  ppt:  '#ea580c', pptx: '#ea580c',
  xls:  '#16a34a', xlsx: '#16a34a',
  txt:  '#64748b', md:   '#64748b',
};

export default function StudentHubPage() {
  const { activeTid } = useStudent();
  const [view, setView] = useState('departments'); // 'departments' | 'subjects' | 'materials'
  const [depts, setDepts] = useState([]);
  const [activeDept, setActiveDept] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [activeSubject, setActiveSubject] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openDoc, setOpenDoc] = useState(null);

  // ── Initial: load departments accessible to this student ────────────────
  useEffect(() => {
    if (!activeTid) return;
    setLoading(true);
    api.get('/hub/departments')
      .then(d => setDepts(d.departments || []))
      .catch(() => setDepts([]))
      .finally(() => setLoading(false));
  }, [activeTid]);

  const openDept = useCallback(async (dept) => {
    setActiveDept(dept);
    setActiveSubject(null);
    setView('subjects');
    setLoading(true);
    try {
      const s = await api.get(`/hub/repo/${dept.id}/subjects`);
      setSubjects(s.subjects || []);
    } catch { setSubjects([]); }
    finally { setLoading(false); }
  }, []);

  const openSubject = useCallback(async (subject) => {
    if (!activeDept) return;
    setActiveSubject(subject);
    setView('materials');
    setLoading(true);
    try {
      const d = await api.get(`/hub/repo/${activeDept.id}/documents?subject_id=${subject.id}&folder_id=*`);
      setMaterials(d.documents || []);
    } catch { setMaterials([]); }
    finally { setLoading(false); }
  }, [activeDept]);

  const back = useCallback(() => {
    if (view === 'materials') { setView('subjects'); setActiveSubject(null); setSearch(''); }
    else if (view === 'subjects') { setView('departments'); setActiveDept(null); setSearch(''); }
  }, [view]);

  const filteredSubjects = useMemo(() => {
    if (!search.trim()) return subjects;
    const q = search.toLowerCase();
    return subjects.filter(s => (s.name || '').toLowerCase().includes(q) || (s.code || '').toLowerCase().includes(q));
  }, [subjects, search]);

  const filteredMaterials = useMemo(() => {
    if (!search.trim()) return materials;
    const q = search.toLowerCase();
    return materials.filter(m => (m.title || '').toLowerCase().includes(q) || (m.file_name || '').toLowerCase().includes(q) || (m.tags || []).some(t => t.toLowerCase().includes(q)));
  }, [materials, search]);

  if (!activeTid) {
    return (
      <div className="rounded-2xl p-6 border-2 text-sm opacity-60"
        style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
        data-testid="hub-no-tenant">
        Switch to an institution to access DeskeyHUB.
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl pb-20" data-testid="student-hub-page">
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            DeskeyHUB
          </h1>
          <p className="text-sm opacity-70 mt-1">
            Books, slide decks and question banks published by your institution — organised by subject and instantly viewable online.
          </p>
        </div>
        <Breadcrumbs view={view} dept={activeDept} subject={activeSubject} onBack={back} />
      </header>

      {/* Search (subject + materials views) */}
      {view !== 'departments' && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            data-testid="hub-search"
            placeholder={view === 'subjects' ? 'Search subjects or codes…' : 'Search materials…'}
            className="pl-9 rounded-xl"
            style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)' }} />
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="text-sm opacity-60" data-testid="hub-loading">Loading…</div>
      ) : view === 'departments' ? (
        <DepartmentsGrid depts={depts} onOpen={openDept} />
      ) : view === 'subjects' ? (
        <SubjectsGrid subjects={filteredSubjects} onOpen={openSubject} />
      ) : (
        <MaterialsList materials={filteredMaterials} onOpen={setOpenDoc}
          deptId={activeDept?.id} subjectCode={activeSubject?.code} subjectName={activeSubject?.name} />
      )}

      {/* Universal viewer */}
      {openDoc && (
        <DocViewer
          doc={openDoc}
          departmentId={activeDept?.id || openDoc.department_id}
          onClose={() => setOpenDoc(null)}
          accent="var(--st-primary)"
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Breadcrumbs
// ════════════════════════════════════════════════════════════════════════

function Breadcrumbs({ view, dept, subject, onBack }) {
  if (view === 'departments') return null;
  return (
    <button onClick={onBack} data-testid="hub-back"
      className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border-2 hover:-translate-y-0.5 transition-all"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
      <ArrowLeft className="w-3.5 h-3.5" />
      Back to {view === 'subjects' ? 'departments' : (dept?.name || 'subjects')}
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Level 1 — Departments
// ════════════════════════════════════════════════════════════════════════

function DepartmentsGrid({ depts, onOpen }) {
  if (depts.length === 0) {
    return (
      <div className="rounded-2xl p-8 border-2 text-sm opacity-60 text-center"
        style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
        No departments accessible to you yet.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="hub-departments-grid">
      {depts.map(d => (
        <button key={d.id} onClick={() => onOpen(d)}
          data-testid={`hub-dept-${d.id}`}
          className="group text-left rounded-2xl border-2 hover:-translate-y-0.5 hover:shadow-lg transition-all relative overflow-hidden flex flex-col"
          style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
          <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: 'linear-gradient(90deg, var(--st-primary), var(--st-accent))' }} />
          <div className="p-5 pb-4 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, var(--st-primary), var(--st-accent))' }}>
                <Library className="w-5 h-5" />
              </div>
              <ArrowRight className="w-4 h-4 mt-2 opacity-0 group-hover:opacity-60 -translate-x-1 group-hover:translate-x-0 transition-all" />
            </div>
            <h3 className="text-base font-bold mt-3 leading-snug">{d.name}</h3>
            <p className="text-[12px] opacity-70 mt-1.5 leading-snug line-clamp-2">
              {d.description || 'Browse subjects and published materials from this department.'}
            </p>
          </div>
          {/* Stat row — surfaces what's actually inside instead of a bare name */}
          <div className="px-5 py-3 border-t flex items-center gap-3 text-[11px]"
            style={{ borderColor: 'var(--st-border)', background: 'var(--st-bg)' }}>
            <span className="inline-flex items-center gap-1 font-semibold" title="Subjects">
              <GraduationCap className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
              {d.subject_count || 0} subject{d.subject_count === 1 ? '' : 's'}
            </span>
            <span className="inline-flex items-center gap-1 opacity-70" title="Published materials">
              <FileText className="w-3.5 h-3.5" />
              {d.document_count || 0}
            </span>
            {d.teacher_contributions > 0 && (
              <span className="inline-flex items-center gap-1 opacity-70 ml-auto" title="Contributing teachers">
                <Users className="w-3.5 h-3.5" />
                {d.teacher_contributions}
              </span>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Level 2 — Subjects (with subject code)
// ════════════════════════════════════════════════════════════════════════

function SubjectsGrid({ subjects, onOpen }) {
  if (subjects.length === 0) {
    return (
      <div className="rounded-2xl p-8 border-2 text-sm opacity-60 text-center"
        style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
        No subjects in this department yet — your teachers will publish materials soon.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="hub-subjects-grid">
      {subjects.map(s => {
        const accent = s.color || 'var(--st-primary)';
        return (
          <button key={s.id} onClick={() => onOpen(s)}
            data-testid={`hub-subject-${s.id}`}
            className="text-left rounded-2xl p-5 border-2 hover:-translate-y-0.5 transition-all relative overflow-hidden"
            style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
            <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: accent }} />
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0"
                style={{ background: accent }}>
                <BookOpen className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                {s.code && (
                  <div className="inline-block text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded font-mono"
                    style={{ background: `${accent}1a`, color: accent }}>
                    {s.code}
                  </div>
                )}
                <h3 className="text-base font-bold mt-1.5 line-clamp-2">{s.name}</h3>
                {s.description && <p className="text-[11px] opacity-70 mt-1 line-clamp-2">{s.description}</p>}
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3 text-[11px] opacity-70">
              <span className="inline-flex items-center gap-1">
                <FileText className="w-3 h-3" /> {s.document_count || 0} materials
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Level 3 — Materials list (instantly online viewable)
// ════════════════════════════════════════════════════════════════════════

function MaterialsList({ materials, onOpen, deptId, subjectCode, subjectName }) {
  if (materials.length === 0) {
    return (
      <div className="rounded-2xl p-8 border-2 text-sm opacity-60 text-center"
        style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
        Nothing published under <b>{subjectName}</b> yet.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="hub-materials-list">
      {materials.map(m => (
        <MaterialRow key={m.id} m={m} deptId={deptId} subjectCode={subjectCode}
          subjectName={subjectName} onOpen={() => onOpen({ ...m, department_id: deptId, subject: subjectName, subject_code: subjectCode })} />
      ))}
    </div>
  );
}

function MaterialRow({ m, deptId, subjectCode, subjectName, onOpen }) {
  const ext = (m.file_ext || (m.file_name || '').split('.').pop() || '').toLowerCase();
  const color = FILE_TYPE_COLORS[ext] || '#64748b';
  const token = localStorage.getItem('desky_token') || '';
  const dlUrl = `${process.env.REACT_APP_BACKEND_URL}/api/hub/repo/${deptId}/documents/${m.id}/download?dl=1&token=${encodeURIComponent(token)}`;
  return (
    <div className="rounded-2xl border-2 px-4 py-3 flex items-center gap-3 hover:-translate-y-0.5 transition-all"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
      data-testid={`hub-material-${m.id}`}>
      {/* File-type chip */}
      <div className="w-12 h-12 rounded-xl flex flex-col items-center justify-center text-white flex-shrink-0"
        style={{ background: color }}>
        <FileType className="w-4 h-4 opacity-90" />
        <span className="text-[8px] font-extrabold uppercase tracking-wider mt-0.5">{ext || 'file'}</span>
      </div>
      {/* Title + meta */}
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-bold truncate">{m.title || m.file_name}</h4>
        {m.file_name && m.file_name !== m.title && (
          <div className="text-[11px] opacity-50 font-mono truncate">{m.file_name}</div>
        )}
        <div className="text-[11px] opacity-60 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
          {m.uploader_name && <span>by {m.uploader_name}</span>}
          {m.published_at && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {new Date(m.published_at).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
          {(m.file_size || 0) > 0 && <span>{formatBytes(m.file_size)}</span>}
          {(m.tags || []).slice(0, 3).map(t => (
            <span key={t} className="px-1.5 py-0 rounded font-mono text-[10px]"
              style={{ background: 'var(--st-bg)', color: 'var(--st-mute)' }}>#{t}</span>
          ))}
        </div>
      </div>
      {/* Actions */}
      <button onClick={onOpen}
        data-testid={`hub-material-view-${m.id}`}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold text-white"
        style={{ background: 'var(--st-primary)' }}>
        <Eye className="w-3.5 h-3.5" /> View
      </button>
      <a href={dlUrl} target="_blank" rel="noopener noreferrer"
        data-testid={`hub-material-download-${m.id}`}
        title="Download"
        className="inline-flex items-center justify-center w-9 h-9 rounded-full border-2 hover:bg-black/5"
        style={{ borderColor: 'var(--st-border)' }}>
        <Download className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}

function formatBytes(b) {
  if (!b) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let n = b;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${units[i]}`;
}
